import React, { useState } from 'react';
import { Button, Typography, Alert } from 'antd';
import { CarOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { setIncidentId } from '../../../redux/reducers/incidentDetailsSlice';
import Api from '../../../services/api';
import Insights from '../../../services/insightsService';

const { Title, Text } = Typography;

const rowStyle: React.CSSProperties = { 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center', 
  padding: '16px 0', 
  borderBottom: '1px solid #f0f0f0' 
};
const labelStyle: React.CSSProperties = { fontSize: 15, color: '#8c8c8c' };

interface VehicleConfirmationStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const VehicleConfirmationStep: React.FC<VehicleConfirmationStepProps> = ({ 
  onNext, 
  onPrevious 
}) => {
  const vehicle = useAppSelector((state) => state.vehicle);
  const existingIncidentId = useAppSelector((state) => state.incidentDetails.incidentId);
  const dispatch = useAppDispatch();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleContinue = async () => {
    if (existingIncidentId > 0) {
      onNext();
      return;
    }

    setCreating(true);
    setCreateError(null);
    try {
      const newIncidentId = await Api.createIncident({
        IncidentDate: new Date().toISOString(),
        OperatorInjured: false,
        VehicleId: vehicle.vehicle?.vehicleId,
        Vin: vehicle.vehicle?.vin || '',
        VehicleNumber: vehicle.vehicle?.vehicleNumber || '',
        DamagedLocations: {},
        DamagedParts: {},
        Photos: [],
        IncidentId: 0,
        Finalize: false,
      });
      dispatch(setIncidentId(newIncidentId));
      onNext();
    } catch (error: unknown) {
      Insights.trackException(error instanceof Error ? error : new Error(
        typeof error === "object" && error !== null
          ? JSON.stringify(error)
          : String(error)
      ));
      setCreateError('Failed to start the incident report. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="step-container">
      <Title level={5} style={{ textAlign: 'center' }}>Vehicle Confirmation</Title>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 0' }}>
        <CarOutlined style={{ fontSize: 48, color: '#8c8c8c' }} />
        <div>
          <Title level={3} style={{ margin: 0 }}>{vehicle.vehicle?.vehicleNumber || ''}</Title>
          <Text type="secondary">
            {vehicle.vehicle?.year || ''} {vehicle.vehicle?.make || ''} {vehicle.vehicle?.model || ''}
          </Text>
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <div style={rowStyle}>
          <Text style={labelStyle}>Vehicle Number</Text>
          <Text>{vehicle.vehicle?.vehicleNumber || ''}</Text>
        </div>
        <div style={rowStyle}>
          <Text style={labelStyle}>VIN</Text>
          <Text>{vehicle.vehicle?.vin || ''}</Text>
        </div>
        <div style={rowStyle}>
          <Text style={labelStyle}>Year</Text>
          <Text>{vehicle.vehicle?.year || ''}</Text>
        </div>
        <div style={rowStyle}>
          <Text style={labelStyle}>Make</Text>
          <Text>{vehicle.vehicle?.make || ''}</Text>
        </div>
        <div style={rowStyle}>
          <Text style={labelStyle}>Model</Text>
          <Text>{vehicle.vehicle?.model || ''}</Text>
        </div>
        <div style={rowStyle}>
          <Text style={labelStyle}>Color</Text>
          <Text>{vehicle.vehicle?.color || ''}</Text>
        </div>
        <div style={rowStyle}>
          <Text style={labelStyle}>Mileage</Text>
          <Text>{vehicle.vehicle?.mileage?.toLocaleString() || ''}</Text>
        </div>
      </div>

      {createError && (
        <Alert
          message="Error Starting Incident"
          description={createError}
          type="error"
          showIcon
          closable
          onClose={() => setCreateError(null)}
          style={{ marginTop: 20 }}
        />
      )}

      <div className="step-actions" style={{ marginTop: 40 }}>
        <Button size="large" onClick={onPrevious} disabled={creating} style={{ color: '#FF9902', fontWeight: 'bold' }}>
          BACK TO VEHICLE SEARCH
        </Button>
        <Button 
          type="primary" 
          size="large"
          icon={<CheckCircleOutlined />}
          onClick={handleContinue}
          loading={creating}
          style={{ background: '#0d5e2e', borderColor: '#0d5e2e', fontWeight: 'bold' }}
        >
          {creating ? 'STARTING INCIDENT...' : 'CONFIRM & CONTINUE'}
        </Button>
      </div>
    </div>
  );
};

export default VehicleConfirmationStep;